import { Injectable, inject } from '@angular/core';
import { MediaPlayerFile } from '../../../../interfaces/media-player-store.interface';
import { MediaPlayerHelperService } from '../../services/media-player-helper.service';
import { SafeResourceUrlService } from '../../../../services/safe-resource-url.service';
import { AuthService } from '../../../../services/auth.service';
import { GoogleAnalyticsService } from '../../../../services/google-analytics.service';
import {
  MTMFileDownloadService,
  onDownloadStart,
} from '../../../../components/mtm-file-download/mtm-file-download.service';
import { FileTransferService } from '../../../../services/signed-url';
import { MTMFileDownload } from '../../../../services/signed-url/mtm-file-download';
import { MediaPlayerStoreService, MediaPlayerStore } from '../../../../services/media-player-store.service';
import { FileUtilsService } from '../../../../services/file-utils.service';
type VideoMetadata = {
  codecType?: string;
  width?: number;
  height?: number;
  codecName?: string;
};

type TranscodeMetadata = {
  codec?: string;
  quality: string;
};

type VideoJsPlayer = {
  updateSrc: (sources: unknown[]) => void;
};

export interface VideoResolution {
  src: string;
  type: string;
  label: string;
  res: string;
}

@Injectable()
export class VideoViewerService {
  private readonly mpStoreService: MediaPlayerStoreService = inject(MediaPlayerStoreService);
  private readonly helperService: MediaPlayerHelperService = inject(MediaPlayerHelperService);
  private readonly safeResourceUrlService: SafeResourceUrlService = inject(SafeResourceUrlService);
  private readonly authService: AuthService = inject(AuthService);
  private readonly gaService: GoogleAnalyticsService = inject(GoogleAnalyticsService);
  private readonly mtmFileDownloadService: MTMFileDownloadService = inject(MTMFileDownloadService);
  private readonly transferService: FileTransferService = inject(FileTransferService);
  private readonly fileUtilsService: FileUtilsService = inject(FileUtilsService);

  /**
   * Set default video options for video.js player
   */
  public setDefaultVideoOptions(
    width: string,
    height: string,
    autoPlayVideo: boolean,
    miniPlayer: boolean,
    instanceId: string
  ): void {
    const store: MediaPlayerStore | null = this.mpStoreService.getStore(instanceId);
    if (!store) return;

    const item: MediaPlayerFile | null = store.itemSignal();

    const rawMetadata: unknown = item?.metadata ? JSON.parse(item.metadata) : null;
    if (!Array.isArray(rawMetadata) || rawMetadata.length === 0) {
      return;
    }

    const videoMetaData: VideoMetadata | undefined = (rawMetadata as VideoMetadata[]).find(
      (m: VideoMetadata) => m?.codecType === 'VIDEO'
    );
    const downloadControlId: string | undefined =
      item.projectId && item.sectionId && item.subSectionId && item.id
        ? `video-js-${item.projectId}-${item.sectionId}-${item.subSectionId}-${item.id}`
        : undefined;
    const videoPreloadMethod: string =
      store.videoPreloadMethodSignal() || (store.enableAnnotationSignal() ? 'auto' : 'metadata');

    const videoOptions: Record<string, unknown> = {
      aspectRatio: '4:3',
      autoplay: autoPlayVideo,
      fluid: true,
      playsinline: true,
      height: typeof height === 'number' ? height : Number.parseInt(height, 10),
      preload: videoPreloadMethod,
      width: Number.parseInt(width, 10),
      controlBar: {
        customPlaybackRateMenu: {} as Record<string, never>,
        playbackRateMenuButton: false,
        children: [
          'playToggle',
          'customPlaybackRateMenu',
          'volumePanel',
          'customControlSpacer',
          'currentTimeDisplay',
          'timeDivider',
          'durationDisplay',
          'customControlSpacer',
          'progressControl',
          'PictureInPictureToggle',
          'fullscreenToggle',
        ],
      },
      plugins: this.getVideoPluginsConfig(item, downloadControlId, videoMetaData, instanceId),
      playbackRates: [0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2],
    };

    if (miniPlayer) {
      (videoOptions.plugins as Record<string, unknown>).threeDotMenu = true;
    }

    store.updateStore({ videoOptions });
  }

  private getVideoPluginsConfig(
    item: MediaPlayerFile,
    downloadControlId: string | undefined,
    videoMetaData: VideoMetadata | undefined,
    instanceId: string
  ): Record<string, unknown> {
    const ignoreQualities: Set<string> = new Set([
      '_SMALL_211_121',
      '_SMALL_209_241',
      '_TINY_211_121',
      '_TINY_209_241',
      '_TINY',
      '_SMALL',
    ]);

    return {
      vjsdownload: {
        controlId: downloadControlId,
        beforeElement: 'ResolutionSwitcher',
        textControl: 'Download',
        name: 'downloadButton',
        isDownloading: downloadControlId ? this.mtmFileDownloadService.isFileInDownload(downloadControlId) : false,
        downloadCallback: (controlId: string, quality?: string) =>
          this.handleVideoDownload(controlId, quality, item, instanceId),
        mediaQuality:
          (item.transcodeMetadata as unknown as TranscodeMetadata[] | undefined)?.filter(
            (m: TranscodeMetadata) => !!m?.quality && !ignoreQualities.has(m.quality)
          ) || [],
        originalMedia: {
          resolution: `${videoMetaData?.width ?? ''}x${videoMetaData?.height ?? ''}`,
          codec: videoMetaData?.codecName,
        },
      },
      videoJsResolutionSwitcher: {
        default: 'second-high',
        dynamicLabel: true,
      },
    };
  }

  private handleVideoDownload(
    controlId: string,
    quality: string | undefined,
    item: MediaPlayerFile,
    instanceId: string
  ): void {
    const cloned: MediaPlayerFile = structuredClone(item);
    if (quality) {
      cloned.displayName = item.displayName.split('.').slice(0, -1).join('.') + '.mp4';
    }
    const button: HTMLElement | null = document.getElementById(controlId);
    if (button) {
      button.classList.add('downloading');
    }
    onDownloadStart.emit();

    const downloadParams: string | null = this.getDownloadParameters(quality, instanceId);
    let url: string | null = null;
    if (item.signedURL) {
      url = (quality ? cloned.signedURL : item.signedURL) + (downloadParams || '');
    }
    const downloadInfo: MTMFileDownload = MTMFileDownload.fromFileRef(quality ? cloned : item);
    downloadInfo.id = controlId;
    downloadInfo.url = url || '';
    this.transferService.downloadFile(downloadInfo);
    this.sendDownloadGAEvent({ url: url || '', quality }, instanceId);
  }

  /**
   * Get download parameters for video download
   */
  private getDownloadParameters(quality: string | undefined, instanceId: string): string | null {
    const store: MediaPlayerStore | null = this.mpStoreService.getStore(instanceId);
    if (!store?.itemSignal()) {
      return null;
    }
    const item: MediaPlayerFile | null = store.itemSignal();
    const params: string[] = [];
    if (item.sectionId) {
      params.push('sectionId=' + item.sectionId);
    }
    if (item.subSectionId) {
      params.push('subSectionId=' + item.subSectionId);
    }
    if (quality) {
      params.push('thumbnail=' + quality);
    }
    return params.length > 0 ? '?' + params.join('&') : null;
  }

  /**
   * Send download event to Google Analytics
   */
  private sendDownloadGAEvent(params: { url: string; quality?: string }, instanceId: string): void {
    const store: MediaPlayerStore | null = this.mpStoreService.getStore(instanceId);
    if (!store?.itemSignal()) {
      return;
    }
    const item: MediaPlayerFile | null = store.itemSignal();

    try {
      const fileName: string = this.fileUtilsService.getFileDisplayName(item?.displayName || item?.name || '');
      const downloadEvent: Record<string, unknown> = {
        fileName,
        fileUrl: params.url,
        fileType: item?.contentType,
        quality: params.quality,
        fileId: item?.id,
        projectId: item?.projectId,
        downloadTime: new Date().toISOString(),
        user: this.authService.getAuthUserName(),
        ...(item.sectionId && { sectionId: item.sectionId }),
        ...(item.subSectionId && { subSectionId: item.subSectionId }),
        ...(item.sourceId && { sourceId: item.sourceId }),
        ...(item.versionNumber && { versionNumber: item.versionNumber }),
      };
      this.gaService.sendCustomEvent('file_download', downloadEvent);
    } catch {
      // no-op
    }
  }

  public setVideoSource(useTranscoded: boolean = false, instanceId: string, playerOverride?: unknown): void {
    if (!instanceId) return;
    const store: MediaPlayerStore | null = this.mpStoreService.getStore(instanceId);
    if (!store) return;

    const item: MediaPlayerFile | null = store.itemSignal();
    const player: any = store.playerSignal();
    const videoPlayer: VideoJsPlayer | null = (playerOverride || player) as VideoJsPlayer | null;
    if (!item || !videoPlayer) {
      return;
    }

    const source: unknown = this.safeResourceUrlService.toSafeUrl(item.signedURL);

    const sourceSafe: string =
      (source as { changingThisBreaksApplicationSecurity?: string }).changingThisBreaksApplicationSecurity || '';
    const type: string = this.helperService.getVideoType(item);

    if (useTranscoded) {
      const resolutionArray: unknown[] = this.buildResolutionArray(item, sourceSafe, type);
      if (resolutionArray.length > 0) {
        if (typeof videoPlayer?.updateSrc === 'function') {
          videoPlayer.updateSrc(resolutionArray);
        } else if (videoPlayer && typeof (videoPlayer as unknown as { src?: unknown })?.src === 'function') {
          (videoPlayer as unknown as { src: (sources: unknown[]) => void }).src(resolutionArray);
        }
        return;
      }
    }

    this.setSourceVideoSource(videoPlayer, sourceSafe, type, store);
  }

  /**
   * Build resolution array for transcoded videos
   */
  public buildResolutionArray(item: MediaPlayerFile, sourceSafe: string, type: string): VideoResolution[] {
    const transcodedVideos: TranscodeMetadata[] = (
      (item.transcodeMetadata as unknown as TranscodeMetadata[] | undefined) || []
    ).filter((m: TranscodeMetadata) => m?.codec !== 'jpeg' && m?.codec !== 'jpg');

    return transcodedVideos
      .map((video: TranscodeMetadata) => {
        const resolution: string = video.quality.substring(1);
        if (resolution !== 'SMALL' && resolution !== 'TINY') {
          return {
            src: sourceSafe + '?thumbnail=_' + resolution,
            type,
            label: resolution + 'p',
            res: resolution,
          };
        }
        return null;
      })
      .filter(Boolean) as VideoResolution[];
  }

  /**
   * Set source video source
   */
  private setSourceVideoSource(player: VideoJsPlayer, sourceSafe: string, type: string, store: MediaPlayerStore): void {
    const sourceOptions: Record<string, unknown> = {
      src: sourceSafe + '?thumbnail=_TRANSCODED',
      type,
      label: 'Source',
    };
    if (!store.isVideoSourceSetSignal()) {
      if (typeof player?.updateSrc === 'function') {
        player.updateSrc([sourceOptions]);
      } else if (player && typeof (player as unknown as { src?: unknown })?.src === 'function') {
        (player as unknown as { src: (sources: unknown[]) => void }).src([sourceOptions]);
      }
      store.updateStore({ isVideoSourceSet: true });
    }
  }

  public handleComparerAction(action: string, video: HTMLVideoElement, instanceId: string): void {
    const store: MediaPlayerStore | null = this.mpStoreService.getStore(instanceId);
    if (!store) return;

    const player: any = store.playerSignal();
    const actions: Record<string, () => void> = {
      setToStart: () => this.seekTo(player, video, 0),
      setToEnd: () => this.seekTo(player, video, player?.duration?.() ?? video.duration),
      pause: () => this.controlPlayback(player, video, 'pause'),
      play: () => this.controlPlayback(player, video, 'play'),
      mute: () => this.setMuted(player, video, true),
      unmute: () => this.setMuted(player, video, false),
    };
    actions[action]?.();
  }

  private seekTo(player: any, video: HTMLVideoElement, time: number): void {
    if ((player as { currentTime?: any })?.currentTime) {
      (player as { currentTime: (t: number) => void }).currentTime(time);
    } else {
      video.currentTime = time;
    }
  }

  private controlPlayback(player: any, video: HTMLVideoElement, method: 'play' | 'pause'): void {
    if ((player as Record<string, any>)?.[method]) {
      (player as Record<string, () => void>)[method]();
    } else {
      video[method]();
    }
  }

  private setMuted(player: any, video: HTMLVideoElement, muted: boolean): void {
    if ((player as { muted?: any })?.muted) {
      (player as { muted: (m: boolean) => void }).muted(muted);
    } else {
      video.muted = muted;
    }
  }

  public reorderControlBarButtons(player: any): void {
    try {
      if (!player?.controlBar?.el) return;
      const barEl: any = player.controlBar.el();
      if (!barEl) return;
      const vacToggle: any = barEl.querySelector('.vac-toggle-annot-display');
      const downloadBtn: any = barEl.querySelector('.vjs-vjsdownload');
      if (vacToggle && downloadBtn && vacToggle !== downloadBtn.previousSibling) {
        barEl.insertBefore(vacToggle, downloadBtn);
      }
    } catch {
      // no-op
    }
  }
}
